import { read } from './http';
import { MOCK_LEADERBOARD, MOCK_LEAGUES } from './mock/leagues.mock';
import type { League, LeaderboardEntry } from '@/types';

/**
 * GET /api/v1/me/leagues
 *
 * Leagues the signed-in manager has entered, with their standing in each. The
 * API's shape matches this app's `League` exactly.
 */
export function getLeagues(signal?: AbortSignal): Promise<readonly League[]> {
  return read('/me/leagues', () => MOCK_LEAGUES, signal);
}

/**
 * GET /api/v1/leagues/:id/standings
 *
 * The endpoint resolves manager display names and flags the signed-in manager's
 * own row, so the board reads correctly without client-side reconciliation.
 *
 * `leagueId` must be a real league id — the API parses it as an ObjectId, so a
 * placeholder like "weekly" is rejected. Use `getPrimaryLeagueStandings` when
 * the caller does not already have one.
 */
export function getLeaderboard(
  leagueId: string,
  limit?: number,
  signal?: AbortSignal,
  period?: StandingsPeriod,
): Promise<readonly LeaderboardEntry[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (period) params.set('period', period);
  const qs = params.toString();

  return read(
    `/leagues/${leagueId}/standings${qs ? `?${qs}` : ''}`,
    () => (limit ? MOCK_LEADERBOARD.slice(0, limit) : MOCK_LEADERBOARD),
    signal,
    // A period with no contest behind it answers empty, and that is the answer.
    { fallbackWhenEmpty: period === undefined },
  );
}

/**
 * Which board `?period=` asks for. The API treats `gameweek` and `weekly` as
 * the same weekly board, and `monthly` as the latest monthly contest in the
 * league's season; a league of that period answers with its own standings.
 */
export type StandingsPeriod = 'weekly' | 'monthly';

/**
 * Standings for the manager's first league.
 *
 * Screens that just want "the leaderboard" have no league id to hand, and the
 * API has no default-league route, so the id is resolved from `/me/leagues`
 * first. Returns the league alongside the rows so the caller can name it.
 */
export async function getPrimaryLeagueStandings(
  limit?: number,
  signal?: AbortSignal,
  period?: StandingsPeriod,
): Promise<{ league: League | null; standings: readonly LeaderboardEntry[] }> {
  const leagues = await getLeagues(signal);
  const league = leagues[0] ?? null;

  if (!league) return { league: null, standings: [] };

  const standings = await getLeaderboard(league.id, limit, signal, period);
  return { league, standings };
}
